import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { FieldMetadata } from 'models/forms/fields';
import { FC, FocusEvent, useCallback, useEffect, useState } from 'react';

interface GridCellProps {
  gridId: string;
  columnIndex: number;
  rowIndex: number;
  metaData: FieldMetadata;
  value: Nullable<string>;
  onValueChanged: (row: number, cellId: string, value: unknown) => void;
  onPaste?: (values: string[][]) => void;
  tabIndex?: number;
}

export const GridCell: FC<GridCellProps> = ({
  rowIndex,
  metaData,
  value = null,
  onValueChanged: onChanged,
  tabIndex,
}) => {
  const onBlur = useCallback(
    (newValue: unknown = null) => {
      if (newValue !== value) {
        onChanged(rowIndex, metaData.id, newValue);
      }
    },
    [value, onChanged, rowIndex, metaData.id],
  );

  const [internalValue, setInternalValue] = useState<Nullable<string>>(value);

  const onInternalChange = useCallback((e: FocusEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value || null);
  }, []);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  switch (metaData.type) {
    case 'TextField':
      return (
        <BlockOrientationFormGroup inputId={metaData.id} label={`form.${metaData.id}` as any}>
          <input
            className="form-control"
            data-e2e={metaData.id + '-input'}
            id={metaData.id}
            name={metaData.id}
            onBlur={e => onBlur(e.target.value)}
            onChange={onInternalChange}
            readOnly={metaData.isReadOnly}
            type="text"
            value={internalValue || ''}
            tabIndex={tabIndex}
          />
        </BlockOrientationFormGroup>
      );
    default:
      return <>{value}</>;
  }
};
GridCell.displayName = 'GridCell';
